/* Fix-it assistant: plain-English diagnosis of a lottery shift's book/scan problems with one-click fixes */
import { useCallback, useEffect, useState } from "react";
import { toast } from "sonner";
import { useNavigate } from "react-router-dom";
import { Wrench, Sparkles, CheckCircle2, AlertTriangle, RefreshCw } from "lucide-react";
import { Btn, Modal } from "@/components/gull/ui";
import { errMsg, money, useLegacyApi } from "@/hooks/useLegacy";
import { BookLink } from "@/pages/lottery/BookDetail";

const SEV = { high: "bg-red-50 border-red-200 text-red-800", medium: "bg-amber-50 border-amber-200 text-amber-900", low: "bg-gray-50 border-gray-200 text-gray-700" };

export const useFixIt = (shiftId, refreshKey) => {
  const api = useLegacyApi();
  const [data, setData] = useState(null);
  const load = useCallback(() => {
    if (!shiftId || String(shiftId).startsWith("0ALL")) { setData(null); return Promise.resolve(); }
    return api.get(`/lotto/${shiftId}/doctor`).then(setData).catch(() => setData(null));
  }, [api, shiftId]);
  useEffect(() => { load(); }, [load, refreshKey]);
  return { data, reload: load, api };
};

const Issue = ({ i, api, shiftId, onDone, readOnly }) => {
  const [busy, setBusy] = useState(null);
  const navigate = useNavigate();
  const run = async (f) => {
    if (f.id === "scan") { navigate(f.note || "/sales-batch"); return; }
    setBusy(f.id);
    try {
      await api.post(`/lotto/${shiftId}/doctor/fix`, { issue_type: i.type, fix: f.id, ga: i.ga, tsid: i.tsid ?? null });
      toast.success(f.id === "keep" ? "Left as is" : `Fixed: ${f.label}`);
      onDone();
    } catch (e) { toast.error(errMsg(e)); } finally { setBusy(null); }
  };
  return (
    <li data-testid={`fixit-issue-${i.ga}`} className={`rounded-xl border p-3 ${SEV[i.severity]}`}>
      <div className="flex items-start gap-2">
        <AlertTriangle className="w-4 h-4 mt-0.5 shrink-0" />
        <div className="flex-1 min-w-0">
          <div className="text-sm font-bold leading-snug">{i.title}</div>
          <p className="text-xs mt-1 opacity-90 leading-relaxed">{i.why}</p>
          <div className="text-[11px] mt-1 opacity-75">
            Book <BookLink id={i.book.Game_Inventory_ID}>{i.book.Book_Serial_Number}</BookLink> · {money(i.book.Ticket_Amount)} tickets · {i.book.Ticket_Quantity} per book
          </div>
          {!readOnly && (
            <div className="flex flex-wrap gap-2 mt-2.5">
              {i.fixes.map((f) => (
                <Btn key={f.id} size="sm" color={f.tone === "danger" ? "danger" : f.tone || "primary"} loading={busy === f.id} disabled={!!busy} data-testid={`fixit-${f.id}-${i.ga}`} onClick={() => run(f)}>
                  {f.recommended && <Sparkles className="w-3.5 h-3.5" />}{f.label}{f.recommended ? " · recommended" : ""}
                </Btn>
              ))}
            </div>
          )}
        </div>
      </div>
    </li>
  );
};

export const FixItList = ({ shiftId, refreshKey, onChanged, readOnly, compact, inModal }) => {
  const { data, reload, api } = useFixIt(shiftId, refreshKey);
  const [busy, setBusy] = useState(false);
  const done = () => { reload(); onChanged?.(); };
  const applyAll = async () => {
    setBusy(true);
    try {
      const r = await api.post(`/lotto/${shiftId}/doctor/apply-recommended`, {});
      toast.success(`${r.applied.length} fix${r.applied.length === 1 ? "" : "es"} applied${r.skipped.length ? ` · ${r.skipped.length} skipped` : ""}`);
      done();
    } catch (e) { toast.error(errMsg(e)); } finally { setBusy(false); }
  };
  if (!data) return null;
  if (!data.issues.length) return compact ? null : (
    <div data-testid="fixit-clean" className="flex items-center gap-2 rounded-xl border border-emerald-200 bg-emerald-50 px-3.5 py-2.5 text-sm font-semibold text-emerald-800"><CheckCircle2 className="w-4 h-4" />Fix-it assistant: no book or scan problems found in this shift.</div>
  );
  return (
    <div data-testid="fixit-panel" className={inModal ? "" : "rounded-2xl border border-[#663399]/20 bg-white shadow-sm p-4"}>
      <div className="flex flex-wrap items-center justify-between gap-2 mb-3">
        <div className="flex items-center gap-2">{!inModal && <span className="w-8 h-8 rounded-lg grid place-items-center bg-[#663399] text-white"><Wrench className="w-4 h-4" /></span>}
          <div>{!inModal && <div className="text-sm font-black text-gray-900">Fix-it assistant</div>}<div className={inModal ? "text-xs text-gray-600" : "text-[11px] text-gray-500"}>{data.issues.length} problem{data.issues.length === 1 ? "" : "s"} found · {data.shift.Shift_Date} · Shift # {data.shift.Shift_Number}{data.shift.finalized ? " · finalized" : ""}</div></div>
        </div>
        <div className="flex gap-2">
          <Btn size="sm" color="outline" onClick={reload} data-testid="fixit-refresh"><RefreshCw className="w-3.5 h-3.5" />Re-check</Btn>
          {!readOnly && data.recommended > 0 && <Btn size="sm" color="success" loading={busy} onClick={applyAll} data-testid="fixit-apply-all"><Sparkles className="w-3.5 h-3.5" />Apply {data.recommended} recommended fix{data.recommended === 1 ? "" : "es"}</Btn>}
        </div>
      </div>
      {readOnly && <p className="text-xs text-amber-800 bg-amber-50 border border-amber-200 rounded-lg px-3 py-2 mb-3">This shift is finalized – unlock it (or turn off the owner-code requirement in Store Settings) to apply fixes.</p>}
      <ul className="space-y-2">{data.issues.map((i) => <Issue key={i.id} i={i} api={api} shiftId={shiftId} onDone={done} readOnly={readOnly} />)}</ul>
    </div>
  );
};

export const FixItButton = ({ shiftId, onChanged, readOnly, refreshKey, label }) => {
  const [open, setOpen] = useState(false);
  const { data } = useFixIt(shiftId, `${refreshKey ?? ""}-${open}`);
  if (!data) return null;
  const n = data.issues.length;
  return (
    <>
      <button type="button" data-testid="fixit-open" onClick={() => setOpen(true)} title={n ? `${n} book problem${n === 1 ? "" : "s"} – open Fix-it assistant` : "Fix-it assistant: no problems found"}
        className={`relative inline-flex items-center justify-center gap-1.5 rounded-full border transition-colors ${label ? "px-3 py-1.5 text-xs font-bold" : "w-9 h-9"} ${n ? "border-red-200 bg-red-50 text-red-700 hover:bg-red-100" : "border-gray-200 bg-white text-gray-400 hover:text-[#663399] hover:border-[#663399]/40"}`}>
        <Wrench className="w-4 h-4" />{label && <span>{label}</span>}
        {n > 0 && <span data-testid="fixit-count" className="absolute -top-1.5 -right-1.5 min-w-[18px] h-[18px] px-1 rounded-full bg-red-600 text-white text-[10px] font-black grid place-items-center shadow">{n}</span>}
      </button>
      <Modal open={open} title="Fix-it assistant" onClose={() => setOpen(false)} testId="fixit-modal">
        <FixItList shiftId={shiftId} refreshKey={open} onChanged={onChanged} readOnly={readOnly} inModal />
      </Modal>
    </>
  );
};
